import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Calendar, MapPin, Clock, Video, Building, Globe, ExternalLink, Trash2, AlertTriangle, FileDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

import AgendaTab from '../components/meeting/AgendaTab';
import CardsTab from '../components/meeting/CardsTab';
import ParkingLotTab from '../components/meeting/ParkingLotTab';
import TimezonesTab from '../components/meeting/TimezonesTab';
import { ChangeRequests } from '../components/ChangeRequests';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MeetingDetailPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { api, isChair, token } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('agenda');
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [meetingRes, membersRes] = await Promise.all([
        api().get(`/meetings/${meetingId}`),
        api().get('/members'),
      ]);
      setMeeting(meetingRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      console.error('Failed to fetch meeting:', error);
      toast.error('Failed to load meeting');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [api, meetingId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteMeeting = async () => {
    try {
      await api().delete(`/meetings/${meetingId}`);
      toast.success('Meeting deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete meeting');
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/meetings/${meetingId}/export-pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${meeting.title.replace(/[^a-z0-9]/gi, '_')}_Agenda.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-600 border-green-500/30';
      case 'completed': return 'bg-gray-500/10 text-gray-600 border-gray-500/30';
      default: return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30';
    }
  };

  const getLocationIcon = () => {
    if (meeting?.location_type === 'online') return <Video className="h-4 w-4" />;
    if (meeting?.location_type === 'physical') return <Building className="h-4 w-4" />;
    return <Globe className="h-4 w-4" />;
  };

  const getLocationLabel = () => {
    if (meeting?.location_type === 'online') return 'Online Meeting';
    if (meeting?.location_type === 'physical') return 'Physical Location';
    return 'Hybrid Meeting';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-40" />
          </div>
        </div>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Meeting not found</p>
        <Button variant="link" onClick={() => navigate('/')}>
          Go back to meetings
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meeting-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/dashboard')}
          className="shrink-0"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">
              {meeting.title}
            </h1>
            <Badge variant="outline" className={getStatusColor(meeting.status)}>
              {meeting.status}
            </Badge>
          </div>
          
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              <span>{format(parseISO(meeting.date), 'EEEE, MMMM d, yyyy')}</span>
            </div>
            {meeting.time && (
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                <span>{meeting.time}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              <span>{meeting.reference_timezone?.replace(/_/g, ' ')}</span>
            </div>
          </div>
          
          {meeting.description && (
            <p className="text-muted-foreground mt-2 line-clamp-2">
              {meeting.description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Export PDF Button */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExportPDF}
            disabled={exporting}
            className="gap-2"
            data-testid="export-pdf-btn"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4" />
            )}
            Export PDF
          </Button>
          
          {isChair && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="icon" className="text-destructive hover:text-destructive" data-testid="delete-meeting-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Meeting</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this meeting? This action cannot be undone. All agenda items and parking lot items will also be deleted.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteMeeting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-btn">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Location Card */}
      <Card data-testid="location-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            {getLocationIcon()}
            {getLocationLabel()}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {(meeting.location_type === 'online' || meeting.location_type === 'hybrid') && meeting.meeting_link && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border">
              <div className="flex items-center gap-2">
                <Video className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Meeting Link</span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2"
                onClick={() => window.open(meeting.meeting_link, '_blank')}
                data-testid="join-meeting-btn"
              >
                <ExternalLink className="h-4 w-4" />
                Join Meeting
              </Button>
            </div>
          )}
          
          {(meeting.location_type === 'physical' || meeting.location_type === 'hybrid') && meeting.location_details && (
            <div className="p-3 rounded-lg bg-muted/50 border">
              <div className="flex items-start gap-2">
                <Building className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <span className="text-sm font-medium">Physical Location</span>
                  <p className="text-sm text-muted-foreground mt-1">{meeting.location_details}</p>
                </div>
              </div>
            </div>
          )}
          
          {!meeting.meeting_link && !meeting.location_details && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-600">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">Location details not yet added</span>
            </div>
          )}
          
          {/* Change Requests */}
          <div className="pt-3 border-t">
            <ChangeRequests 
              meetingId={meetingId} 
              meetingData={meeting} 
              onChangeApplied={fetchData}
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto" data-testid="meeting-tabs">
          <TabsTrigger value="agenda" data-testid="agenda-tab">Agenda</TabsTrigger>
          <TabsTrigger value="cards" data-testid="cards-tab">Cards</TabsTrigger>
          <TabsTrigger value="parking-lot" data-testid="parking-lot-tab">Parking Lot</TabsTrigger>
          <TabsTrigger value="timezones" data-testid="timezones-tab">Timezones</TabsTrigger>
        </TabsList>

        <TabsContent value="agenda" className="mt-6">
          <AgendaTab 
            meetingId={meetingId} 
            meeting={meeting}
            members={members}
          />
        </TabsContent>

        <TabsContent value="cards" className="mt-6">
          <CardsTab meetingId={meetingId} />
        </TabsContent>

        <TabsContent value="parking-lot" className="mt-6">
          <ParkingLotTab meetingId={meetingId} members={members} />
        </TabsContent>

        <TabsContent value="timezones" className="mt-6">
          <TimezonesTab 
            meetingId={meetingId} 
            meeting={meeting}
            members={members}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
